import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import guardianPropertySearchService from '../services/guardianPropertySearchService';
import { toast } from 'react-toastify';
import styles from '../assets/guardianPropertySearch.module.css';
import GoogleMap from '../components/GoogleMap';

const GuardianPropertySearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    bedrooms: '',
    price_min: '',
    price_max: '',
    location: '',
  });
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lng: -0.1278 }); // London
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 8,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    fetchPropertiesWithoutFilters();
  }, [pagination.currentPage]);

  const fetchPropertiesWithoutFilters = async () => {
    try {
      setLoading(true);
      const searchParams = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      };
      const data = await guardianPropertySearchService.searchProperties(
        searchParams
      );
      setProperties(data.properties || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.totalPages || 1,
        totalItems: data.totalItems || 0,
        hasNextPage: data.hasNextPage || false,
        hasPrevPage: data.hasPrevPage || false,
      }));
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const searchParams = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      };
      const data = await guardianPropertySearchService.searchProperties(
        searchParams
      );
      setProperties(data.properties || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.totalPages || 1,
        totalItems: data.totalItems || 0,
        hasNextPage: data.hasNextPage || false,
        hasPrevPage: data.hasPrevPage || false,
      }));
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Apply filters and search
    fetchProperties();
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleRequestViewing = async (propertyId) => {
    try {
      await guardianPropertySearchService.requestViewing(propertyId);
      toast.success('Viewing request sent successfully');
      // Refresh properties to update button states
      fetchPropertiesWithoutFilters();
    } catch (error) {
      console.error('Error requesting viewing:', error);
      toast.error('Failed to request viewing');
    }
  };

  const handleMoreInformation = (propertyId) => {
    // Navigate to property details page
    navigate(`/guardian-property-details/${propertyId}`);
  };

  const PropertyCard = ({ property }) => (
    <div className={styles.propertyCard}>
      <div className={styles.propertyHeader}>
        <button className={styles.priceButton}>£{property.price}</button>
        <div className={styles.propertyBadge}>{property.bedrooms}</div>
      </div>

      <div className={styles.propertyInfo}>
        <h3 className={styles.propertyTitle}>PRICE</h3>
        <p className={styles.propertyPrice}>£{property.price}</p>
        <p className={styles.propertyLocation}>{property.location}</p>
        <p className={styles.propertyAddress}>{property.address}</p>
      </div>

      <div className={styles.propertyStats}>
        <div className={styles.statItem}>
          <div className={styles.statIcon}>🛒</div>
          <div className={styles.statLabel}>TOTAL SPACES</div>
          <div className={styles.statValue}>{property.total_spaces || 1}</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statIcon}>🍴</div>
          <div className={styles.statLabel}>AVAILABLE SPACES</div>
          <div className={styles.statValue}>
            {property.available_spaces || 1}
          </div>
        </div>
      </div>

      <div className={styles.propertyActions}>
        <button
          className={`${styles.actionButton} ${styles.viewButton}`}
          onClick={() => navigate('/keep-me-informed')}
        >
          VIEW NOTIFICATIONS
        </button>
        <button
          className={`${styles.actionButton} ${styles.infoButton}`}
          onClick={() => handleMoreInformation(property.id)}
        >
          MORE INFORMATION
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading properties...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Map Section */}
      <div className={styles.mapSection}>
        <div className={styles.mapContainer}>
          <GoogleMap
            center={mapCenter}
            zoom={12}
            properties={properties}
            onPropertyClick={(propertyId) => handleMoreInformation(propertyId)}
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          />
        </div>
      </div>

      {/* Request a Viewing Section */}
      <div className={styles.viewingSection}>
        <h3>Request a Viewing</h3>
        <p>
          Properties shown below are available and we recommend choosing the one
          that best fits your lifestyle and budget.
        </p>
        <ul>
          <li>
            ✓ Request Viewing - Get a 48 hours viewing window for each available
          </li>
          <li>
            ✓ View Space and Timing - Choose a preferred time and space from
            live availability
          </li>
          <li>
            ✓ Meet the location contact - Meet location contact at the property
            during your viewing and complete your application
          </li>
        </ul>
        <p>
          You will need to see all your properties so you can think hard to find
          the date? We can then provide the viewing needs you need. Any
          questions can be sent to housing@my-globe.co.uk
        </p>
      </div>

      {/* Filters Section */}
      <div className={styles.filtersSection}>
        <h3>Viewing / Cost preferences</h3>
        <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Select your preferences below and click "Apply Filters & Search" to
          see matching properties.
        </p>
        <div className={styles.filterGrid}>
          <div className={styles.filterGroup}>
            <label>Search</label>
            <input
              type="text"
              placeholder="Search properties..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>Bedrooms</label>
            <select
              value={filters.bedrooms}
              onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">Any</option>
              <option value="1">1 Bedroom</option>
              <option value="2">2 Bedrooms</option>
              <option value="3">3 Bedrooms</option>
              <option value="4+">4+ Bedrooms</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>Min Price</label>
            <input
              type="number"
              placeholder="£0"
              value={filters.price_min}
              onChange={(e) => handleFilterChange('price_min', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>Max Price</label>
            <input
              type="number"
              placeholder="£1000"
              value={filters.price_max}
              onChange={(e) => handleFilterChange('price_max', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <button onClick={handleSearch} className={styles.searchButton}>
              Apply Filters & Search
            </button>
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className={styles.propertiesSection}>
        <div className={styles.propertiesGrid}>
          {properties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>

        {properties.length === 0 && !loading && (
          <div className={styles.noProperties}>
            <p>No properties found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <div className={styles.paginationNumbers}>
            {pagination.hasPrevPage && (
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                ‹ Previous
              </button>
            )}

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  className={`${styles.pageButton} ${
                    pagination.currentPage === page ? styles.activePage : ''
                  }`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )
            )}

            {pagination.hasNextPage && (
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuardianPropertySearch;
